import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, Check, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { GATE_COPY, usd } from "@/lib/pricing";
import { dateLabel } from "@/lib/fmt";

export const PLAN_NAMES = { essential: "Essential", ops_pro: "Operations Pro", group: "Group", founding: "Founding Restaurant" };
export const PAID_STATES = ["active", "past_due", "past_due_locked"];

export function FeatureGate({ feature, children }) {
  const { features, session, featurePlans } = useAuth();
  if (session && !features.includes(feature)) return <UpgradeGate detail={`upgrade_required:${featurePlans[feature] || "ops_pro"}:${feature}`} />;
  return children;
}

function UpgradeCost({ plan }) {
  const { state, org } = useAuth();
  const subscribed = PAID_STATES.includes(state?.status) && !!org?.stripe_subscription_id;
  const interval = org?.billing_interval || "month";
  const q = useQuery({
    queryKey: ["upgrade-cost", plan, interval, subscribed],
    queryFn: () => (subscribed ? api.post("/billing/preview-change", { plan, interval }) : api.post("/billing/quote", { plan, interval, locations: 1 })).then((r) => r.data),
    staleTime: 60000, retry: false,
  });
  if (q.isLoading) return <div className="text-xs text-stone-500 mt-4 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Checking the exact price…</div>;
  if (q.isError || !q.data) return <p className="text-xs text-stone-500 mt-4" data-testid="upgrade-cost-unavailable">You'll see the exact amount before you confirm anything.</p>;
  const per = interval === "year" ? "yr" : "mo";
  if (!subscribed) return <p className="text-sm mt-4" data-testid="upgrade-cost">{PLAN_NAMES[plan]}: <b className="num">{usd(q.data.recurring_total_cents)}/{per}</b> <span className="text-stone-500">plus applicable tax · shown again on the secure payment page before you pay.</span></p>;
  const x = q.data.quote;
  if (!x) return null;
  return (
    <div className="mt-4 rounded-lg bg-[#F3EFE6] p-3 text-sm" data-testid="upgrade-cost">
      <div>Due now: <b className="num" data-testid="upgrade-cost-due">{usd(x.amount_due_now_cents)}</b> <span className="text-stone-500">(prorated; credit for unused time {usd(x.credit_cents)})</span></div>
      <div className="text-stone-600">Then {usd(x.renewal_amount_cents)}/{per} from {dateLabel(x.renewal_date)} plus applicable tax. Features unlock after Stripe confirms payment.</div>
    </div>
  );
}

export default function UpgradeGate({ detail }) {
  const { user } = useAuth();
  const [kind, plan, feature] = detail.split(":");
  const both = kind === "permission_and_plan_required";
  const copy = GATE_COPY[feature] || { title: "This feature", points: [] };
  const name = PLAN_NAMES[plan] || "a higher plan";
  const owner = user?.role === "owner";
  if (both) return (
    <div data-testid="plan-and-permission-gate" className="card-surface p-6 md:p-8 max-w-2xl">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500"><Lock className="h-3.5 w-3.5" />Not available to your account</div>
      <h2 className="font-display text-2xl md:text-3xl mt-2">{copy.title}</h2>
      <p className="text-sm text-stone-600 mt-2">Two things are needed: the restaurant's plan must include {name}, and your account needs permission for this area. Ask the owner if you need it.</p>
    </div>
  );
  return (
    <div data-testid="upgrade-gate" className="card-surface p-6 md:p-8 max-w-2xl">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500"><Lock className="h-3.5 w-3.5" />Requires the {name} plan</div>
      <h2 className="font-display text-2xl md:text-3xl mt-2">{copy.title}</h2>
      <p className="text-sm text-stone-600 mt-2">{owner ? "Your current plan doesn't include this." : "You have permission for this area, but the restaurant's current plan doesn't include it."} With {name} you get:</p>
      <ul className="mt-4 space-y-2 text-sm">{copy.points.map((p) => <li key={p} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-emerald-700" />{p}</li>)}</ul>
      <p className="text-xs text-stone-500 mt-4">Any data you've already entered is kept.</p>
      {owner ? <>
        <UpgradeCost plan={plan} />
        <div className="mt-6 flex flex-wrap gap-2">
          <Link to={`/app/billing?change=${plan}`} className="h-11 px-5 rounded-full bg-stone-900 text-white text-sm font-semibold inline-flex items-center" data-testid="upgrade-gate-cta">View plans &amp; upgrade</Link>
        </div>
      </> : <p className="mt-6 text-sm font-medium" data-testid="upgrade-ask-owner">Ask your restaurant owner about upgrading to {name}.</p>}
    </div>
  );
}
